import { get, isEmpty } from 'lodash';

import { getTranslatedString } from '@skinsoft/ui';

import { first } from 'rxjs/operators';
import { ThesaurusTermDataStore } from '../thesaurusTerm';
import { preferredTerm } from '../thesaurusTerm/selectors';
import { Manifestation } from './manifestation-data-store';

export const id = (manifestation: Manifestation) => get(manifestation, 'id');

export const expression = (manifestation: Manifestation) => get(manifestation, ['linkToExpression', 'referentExpression']);

export const title = (manifestation: Manifestation, locale: string) => getTranslatedString(get(manifestation, 'descriptionSchema.titles[0].title'), locale)

export const domain = (manifestation: Manifestation) => get(manifestation, 'descriptionSchema.domain[0]')

export const name = (manifestation: Manifestation) => get(manifestation, 'descriptionSchema.name[0]')

/**
 * Builds a textual summary of all materials and techniques
 * as an array of strings
 * @param manifestation 
 * @param locale 
 */
export const materialsAndTechnics = async (manifestation: Manifestation, locale: string) => {
    const matAndTechs = get(manifestation, 'descriptionSchema.materialAndTechniques')
    const resolvedMAndT: string[] = [];
    if (matAndTechs) {
        for (const mt of matAndTechs) {
            const materials = mt.materials;
            const techniques = mt.techniques;

            let materialsSummary: string = "";
            let matIndex = 0;
            // Looping through materials
            if (materials) {
                for (const mat of materials) {
                    // material
                    let matTitle = null
                    if (mat.title && typeof mat.title === 'string') {
                        const matTerm = await ThesaurusTermDataStore.getInstance().fetchOne(mat.material).pipe(first()).toPromise()
                        matTitle = getTranslatedString(preferredTerm(matTerm), locale)
                    }

                    // color
                    let colorTitle = null;
                    if (mat.color && typeof mat.color === 'string') {
                        const colorTerm = await ThesaurusTermDataStore.getInstance().fetchOne(mat.color).pipe(first()).toPromise()
                        colorTitle = getTranslatedString(preferredTerm(colorTerm), locale)
                    }

                    // precision
                    let precision = null;
                    if (mat.precision) {
                        precision = getTranslatedString(mat.precision, locale)
                    }

                    const matSummary: string = matTitle ? matTitle : ''
                        + (colorTitle ? ` ${colorTitle}` : "")
                        + (precision ? ` (${precision})` : "")

                    if (matIndex > 0) {
                        materialsSummary += ", "
                    }

                    materialsSummary += matSummary;
                    matIndex++;
                }
            }
            let technicsSummary: string = "";
            let techIndex = 0;
            if (techniques) {
                for (const tech of techniques) {
                    // technique
                    let techTitle = null
                    if (tech.title || typeof tech.title === 'string') {
                        const techTerm = await ThesaurusTermDataStore.getInstance().fetchOne(tech.technique).pipe(first()).toPromise()
                        techTitle = getTranslatedString(preferredTerm(techTerm), locale)
                    }

                    // precision
                    let precision = null;
                    if (tech.precision) {
                        precision = getTranslatedString(tech.precision, locale)
                    }

                    const techSummary: string = techTitle
                        + (precision ? ` (${precision})` : "")

                    if (techIndex > 0) {
                        technicsSummary += ", "
                    }
                    technicsSummary += techSummary
                    techIndex++;
                }
            }
            resolvedMAndT.push(materialsSummary + (technicsSummary ? ` / ${technicsSummary}` : ""))
        }
    }
    return resolvedMAndT;
}

/**
 * Builds a summary of measurements as an array of strings
 * @param manifestation 
 * @param locale 
 */
export const measurements = async (manifestation: Manifestation, locale: string) => {
    const measures = get(manifestation, 'descriptionSchema.measurements')
    const measuresSummary: string[] = []
    if (measures) {
        for (const measurementGroup of measures) {
            if (measurementGroup && measurementGroup.measures) {
                for (const measure of measurementGroup.measures) {
                    let measureType: string = null as any;
                    let measureValue: string = null as any;
                    let measureUnit: string = null as any;
                    if (measure.typeOfMeasure) {
                        const term = await ThesaurusTermDataStore.getInstance().fetchOne(measure.typeOfMeasure).pipe(first()).toPromise()
                        measureType = getTranslatedString(preferredTerm(term), locale)
                    }
                    if (measure.value) {
                        measureValue = getTranslatedString(measure.value, locale) as string
                    }
                    if (measure.unit) {
                        const term = await ThesaurusTermDataStore.getInstance().fetchOne(measure.unit).pipe(first()).toPromise()
                        measureUnit = getTranslatedString(preferredTerm(term), locale)
                    }
                    if (measureValue) {
                        const measureSummary =
                            (measureType ? `${measureType} : ` : "")
                            + measureValue
                            + (measureUnit ? ` ${measureUnit}` : "")

                        measuresSummary.push(measureSummary);
                    }
                }
            }
        }
    }
    return measuresSummary;
}